import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { DataGrid } from "@mui/x-data-grid";
import { styled } from '@mui/material/styles';
import { Button, Checkbox, FormControl, MenuItem, OutlinedInput, Select, Stack, Tooltip, TooltipProps, Typography, tooltipClasses } from "@mui/material";

import { api } from "../../../../../utils";
import { IError, ReportLoadingType } from "../../../../../models/ReportPageModels";
import { CustomNoRowsOverlay, PaginationModel } from "../../../../../models/ReportGridModels";

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 'none',
    },
});

interface BillingClientsResponseModel {
    data: BillingClientsData;
    error: IError;
}
interface BillingClientsData {
    clients: string[];
}

interface BillingStatusRequestModel {
    usernames: string[]
}
interface BillingStatusResponseModel {
    data: BillingStatusData;
    error: IError;
}
interface BillingStatusData {
    status: BillingStatusModel[]
}
interface BillingStatusModel {
    username: string;
    setType: string;
    boothId: string;
    amount: string;
}

const columns = [
    { field: 'id', headerName: 'ID', width: 10 },
    { field: 'username', headerName: 'Client name', width: 200 },
    { field: 'setType', headerName: 'Type', width: 200, },
    { field: 'boothId', headerName: 'BoothId', width: 300 },
    {
        field: 'amount', headerName: 'Amount (for past 3 months)', width: 700,
        renderCell: (params: any) => (
            <>
                {params.value.length != 0 ?
                    <>
                        <NoMaxWidthTooltip
                            placement="bottom-start"
                            title={
                                <Typography
                                    fontSize={"16px"}
                                    sx={{ whiteSpace: "pre-line" }}>
                                    {params.value}
                                </Typography>
                            }>
                            <p>{params.value}</p>
                        </NoMaxWidthTooltip>
                    </>
                    : null
                }
            </>
        )
    },
];
const columnVisibility = {
    id: false
}

function BillingStatus() {
    const { enqueueSnackbar } = useSnackbar();
    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 50,
        page: 0,
    });

    const [billingLoadingType, setBillingLoadingType] = useState<ReportLoadingType>(ReportLoadingType.SelectClient); // loading, error, noreports, selectClient

    const [allClients, setAllClients] = useState<string[]>([]);
    const [specificClients, setSpecificClients] = useState<string[]>([]);

    const [statusResult, setStatusResult] = useState<BillingStatusModel[]>([]);

    useEffect(() => {
        LoadClients();
    }, []);

    const LoadClients = async () => {
        try {
            var resp: BillingClientsResponseModel = await api.get("api/Billing/GetClients", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setAllClients(resp.data.clients);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const CheckBillingStatus = async () => {
        setStatusResult([]);
        setBillingLoadingType(ReportLoadingType.Loading)
        var body: BillingStatusRequestModel = {
            usernames: specificClients
        }
        try {
            var resp: BillingStatusResponseModel = await api.post("api/Billing/BillingStatus", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setStatusResult(resp.data.status);
                setBillingLoadingType(ReportLoadingType.Loaded);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
                setBillingLoadingType(ReportLoadingType.Error)
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
            setBillingLoadingType(ReportLoadingType.Error)
        }
    }

    function GetGridHeight() {
        return window.innerHeight - 112;
    }

    return (
        <Stack direction={"row"}>
            <Stack
                direction={"column"}
                alignItems={"start"}>

                {/* Billing per */}
                <span style={{ width: '200px' }}>Select clients: </span>

                <FormControl sx={{ minWidth: '246px', width: '246px', marginBottom: "10px" }}>
                    <Select
                        multiple
                        value={specificClients}
                        onChange={(event: any) => setSpecificClients(event.target.value)}
                        size="small"
                        sx={{ height: "40px" }}
                        input={<OutlinedInput label="Tag" />}
                        renderValue={(selected) => selected.join(', ')}>
                        {allClients.map((item: string, index: number) => (
                            <MenuItem
                                key={index}
                                value={item}>
                                <Checkbox sx={{ padding: "0px" }} checked={specificClients.indexOf(item) > -1} />
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button
                    variant="outlined"
                    onClick={CheckBillingStatus}>
                    Check status
                </Button>
            </Stack>

            <DataGrid
                sx={{ height: GetGridHeight(), width: "100%", marginLeft: "15px", }}
                rows={statusResult}
                columns={columns}
                columnVisibilityModel={columnVisibility}
                pagination
                pageSizeOptions={[10, 15, 20, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                slots={{
                    noRowsOverlay: CustomNoRowsOverlay
                }}
                slotProps={{
                    noRowsOverlay: {
                        reportLoadingType: billingLoadingType
                    }
                }} />

        </Stack>
    )
}

export default BillingStatus;